import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { QuestionProps } from "@src/components/task-room-details/data.types";
import { SubmissionResult } from "./studentTournamentSlice";

export interface ElpResponseProps {
  userCode: Record<string, string> | null | string;
  questionId?: string;
  marks?: number;
  batchId?: string;
  studentId?: string;
  versionId?: string;
  questionProgramType?: string | null;
  language?: string;
  userId?: string;
  answer?: string;
  blankOptions?: Record<string, string[]>;
  matching?: Record<string, string>;
  numericValue?: number;
  options?: string[];
  numerator?: number;
  denominator?: number;
  isThickClient?: boolean;
  studentMarks?: number;
  result?: string;
  output?: string;
}

interface ElpLastSubmission {
  [batchId: string]: {
    [id: string]: SubmissionResult;
  };
}

interface InitialState {
  currentElpResponse: ElpResponseProps | null;
  elpResponseList: ElpResponseProps[];
  currentElpQuestion: QuestionProps | null;
  elpQuestions: QuestionProps[];
  hasShownToast: boolean;
  isTimerStarted: boolean;
  selectedTab: string;
  currentIndex: number;
  elptLastSubmissions: ElpLastSubmission;
}

const initialState: InitialState = {
  currentElpResponse: null,
  elpResponseList: [],
  currentElpQuestion: null,
  elpQuestions: [],
  hasShownToast: false,
  isTimerStarted: false,
  selectedTab: "Questions",
  currentIndex: 0,
  elptLastSubmissions: {},
};

const elpQuestionSlice = createSlice({
  name: "elpQuestion",
  initialState,
  reducers: {
    setElpResponseList: (state, action: PayloadAction<ElpResponseProps[]>) => {
      state.elpResponseList = action.payload;
      state.currentElpResponse = action.payload[0];
    },
    updateElpResponseList: (state, action: PayloadAction<ElpResponseProps>) => {
      const { questionId } = action.payload;
      const index = state.elpResponseList.findIndex(
        (res) => res.questionId === questionId
      );
      if (index != -1) {
        state.elpResponseList[index] = action.payload;
      }
    },
    updateCurrentElpResponse: (
      state,
      action: PayloadAction<ElpResponseProps>
    ) => {
      const { questionId } = action.payload;
      if (!state.currentElpResponse || state.currentElpResponse?.questionId === questionId) {
        state.currentElpResponse = action.payload;
      }
    },
    setCurrentElpResponse: (state, action: PayloadAction<ElpResponseProps>) => {
      const selectedResponse = state.elpResponseList.find(
        (resp) => resp?.questionId === action.payload?.questionId
      );
      state.currentElpResponse = selectedResponse || action.payload || null;
    },
    setElpQuestions: (state, action: PayloadAction<QuestionProps[]>) => {
      state.elpQuestions = action.payload;
    },
    setCurrentElpQuestion: (
      state,
      action: PayloadAction<QuestionProps | null>
    ) => {
      if (!action.payload) {
        state.currentElpQuestion = null;
        return;
      }
      const selectedQuestion = state.elpQuestions.find(
        (q) => q.id === action.payload?.id
      );
      // Prefer action.payload if it has full question data, or fallback to selectedQuestion in list
      state.currentElpQuestion = (action.payload.questionTitle || action.payload.version)
        ? action.payload
        : (selectedQuestion || action.payload);
    },
    setHasShownToast: (state, action: PayloadAction<boolean>) => {
      state.hasShownToast = action.payload;
    },
    setIsTimerStarted: (state, action: PayloadAction<boolean>) => {
      state.isTimerStarted = action.payload;
    },
    setSelectedTab(state, action) {
      state.selectedTab = action.payload;
    },
    setCurrentIndex(state, action) {
      state.currentIndex = action.payload;
    },
    addElpLastSubmissions: (state, action) => {
      const { questionId, batchId } = action.payload;

      if (!state.elptLastSubmissions) {
        state.elptLastSubmissions = {};
      }

      if (!state.elptLastSubmissions[batchId]) {
        state.elptLastSubmissions[batchId] = {};
      }

      state.elptLastSubmissions[batchId][questionId] = action.payload;
    },
  },
});

export const {
  setElpResponseList,
  updateElpResponseList,
  setCurrentElpQuestion,
  setElpQuestions,
  setCurrentElpResponse,
  updateCurrentElpResponse,
  setHasShownToast,
  setIsTimerStarted,
  setSelectedTab,
  setCurrentIndex,
  addElpLastSubmissions,
} = elpQuestionSlice.actions;
export default elpQuestionSlice.reducer;
